/**
 * User Events Logger
 *
 * Handles logging of user-related events to PostHog Analytics.
 * Includes authentication, organization membership, and watchlist events.
 * @module features/analytics/utils/userEventLogger
 */
import { ANALYTICS_EVENTS } from "../constants";
import type {
  TUserSignInParams,
  TUserSignOutParams,
  TOrganizationJoinedParams,
  TOrganizationLeftParams,
  TPlayerWatchlistParams,
  TWatchlistManagementParams,
  TTeamSwitchedParams,
  TOrganizationCreatedParams,
  TOrganizationUpdatedParams,
  TUserInvitedParams,
  IUserAnalyticsService,
} from "../types";
import { sendAnalyticsEvent } from "./posthogSender";

/**
 * User events analytics logger.
 *
 * Provides methods for logging user lifecycle events including:
 * - Authentication (sign in/out)
 * - Organization membership (join/leave)
 * - Watchlist management (create/update/delete)
 * - Player watchlist actions (add/remove players)
 * @example
 * ```typescript
 * import { userEventLogger } from '@/features/analytics';
 *
 * userEventLogger.logUserSignIn({
 *   method: 'phone',
 *   phone_number: '+1234567890',
 *   // ... other params
 * });
 * ```
 */
export const userEventLogger: IUserAnalyticsService = {
  /**
   * Logs a user sign-in event.
   * @param params - Sign-in parameters
   */
  logUserSignIn: async (params: TUserSignInParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.SIGN_IN, params);
  },

  /**
   * Logs a user sign-out event.
   * @param params - Sign-out parameters
   */
  logUserSignOut: async (params: TUserSignOutParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.SIGN_OUT, params);
  },

  /**
   * Logs an organization joined event.
   * @param params - Organization joined parameters
   */
  logOrganizationJoined: async (
    params: TOrganizationJoinedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.ORGANIZATION_JOINED, params);
  },

  /**
   * Logs an organization left event.
   * @param params - Organization left parameters
   */
  logOrganizationLeft: async (
    params: TOrganizationLeftParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.ORGANIZATION_LEFT, params);
  },

  /**
   * Logs a player added to watchlist event.
   * @param params - Player watchlist parameters
   */
  logPlayerWatchlistAdded: async (
    params: TPlayerWatchlistParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.USER.PLAYER_WATCHLIST_ADDED,
      params
    );
  },

  /**
   * Logs a player removed from watchlist event.
   * @param params - Player watchlist parameters
   */
  logPlayerWatchlistRemoved: async (
    params: TPlayerWatchlistParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.USER.PLAYER_WATCHLIST_REMOVED,
      params
    );
  },

  /**
   * Logs a watchlist creation event.
   * @param params - Watchlist management parameters
   */
  logWatchlistCreated: async (
    params: TWatchlistManagementParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.WATCHLIST_CREATED, params);
  },

  /**
   * Logs a watchlist deletion event.
   * @param params - Watchlist management parameters
   */
  logWatchlistDeleted: async (
    params: TWatchlistManagementParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.WATCHLIST_DELETED, params);
  },

  /**
   * Logs a watchlist update event.
   * @param params - Watchlist management parameters
   */
  logWatchlistUpdated: async (
    params: TWatchlistManagementParams
  ): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.WATCHLIST_UPDATED, params);
  },

  /**
   * Logs an active-team switch event.
   * @param params - Team switched parameters
   */
  logTeamSwitched: async (params: TTeamSwitchedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.TEAM_SWITCHED, params);
  },

  /**
   * Logs an organization created event (SE-4010).
   * @param params - Organization created parameters
   */
  logOrganizationCreated: async (
    params: TOrganizationCreatedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.USER.ORGANIZATION_CREATED,
      params
    );
  },

  /**
   * Logs an organization updated event (SE-4010).
   * @param params - Organization updated parameters
   */
  logOrganizationUpdated: async (
    params: TOrganizationUpdatedParams
  ): Promise<void> => {
    await sendAnalyticsEvent(
      ANALYTICS_EVENTS.USER.ORGANIZATION_UPDATED,
      params
    );
  },

  /**
   * Logs a user invited event (SE-4010).
   * @param params - User invited parameters
   */
  logUserInvited: async (params: TUserInvitedParams): Promise<void> => {
    await sendAnalyticsEvent(ANALYTICS_EVENTS.USER.INVITED, params);
  },
};
